import { useEffect, useRef } from "react";
import type { StationV2 } from "@orvis/core";
import { OrvisBackgroundAudio, hasNative } from "../native.js";

/**
 * Keep radio alive (and controllable) with the app in the background on
 * Android: mirrors the current station into RadioPlaybackService's media
 * notification, and routes its buttons (and headset keys) back here.
 * No-op on the web, iOS (UIBackgroundModes=audio already covers it) and
 * Android TV.
 */
export function useBackgroundAudio(
  station: StationV2 | undefined,
  opts: {
    paused: boolean;
    songTitle?: string;
    enabled: boolean;
    onPlay: () => void;
    onPause: () => void;
    onStop: () => void;
    onNext: () => void;
    onPrev: () => void;
  }
): void {
  const native = hasNative("OrvisBackgroundAudio") && opts.enabled;
  const handlers = useRef(opts);
  handlers.current = opts;

  useEffect(() => {
    if (!native) return;
    const handle = OrvisBackgroundAudio.addListener("control", ({ action }) => {
      const h = handlers.current;
      if (action === "play") h.onPlay();
      else if (action === "pause") h.onPause();
      else if (action === "stop") h.onStop();
      else if (action === "next") h.onNext();
      else if (action === "previous") h.onPrev();
    });
    return () => void handle.then((h) => h.remove());
  }, [native]);

  useEffect(() => {
    if (!native) return;
    if (!station) {
      void OrvisBackgroundAudio.stop().catch(() => {});
      return;
    }
    void OrvisBackgroundAudio.update({
      title: opts.songTitle ?? station.name,
      subtitle: opts.songTitle ? station.name : station.description ?? "",
      artwork: station.logo || undefined,
      playing: !opts.paused,
    }).catch((err) => console.warn("[bg-audio]", err));
  }, [native, station, opts.paused, opts.songTitle]);

  // Leaving the radio screen (or the app tearing down) stops the service.
  useEffect(() => {
    if (!native) return;
    return () => void OrvisBackgroundAudio.stop().catch(() => {});
  }, [native]);
}
